import { type JSX } from "react";
import styles from "@/components/ClientOfferView.module.css";
import type { OfferSectionProps } from "@/components/offer/offerDocumentTypes";
import {
  fmtPln,
  groupPayableRows,
  lineUnitNetAfterMargin,
  pluralPozycje,
} from "@/components/offer/offerDocumentUtils";
import { lineFinalPrice } from "@/utils/mvpEstimateMath";

export function EstimateTableSection({ offerData }: OfferSectionProps): JSX.Element {
  const groups = groupPayableRows(offerData.lines);
  const lines = groups.flatMap((g) => g.lines);
  const compact = offerData.templateType === "quick";

  let globalIdx = 0;
  const tableBodyRows = groups.flatMap((group, groupIdx) => [
    group.title ? (
      <tr key={`group-${groupIdx}`}>
        <td
          colSpan={compact ? 3 : 6}
          style={{
            paddingTop: "16px",
            paddingBottom: "4px",
            fontWeight: 600,
            fontSize: "11px",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "#888",
            borderBottom: "none",
          }}
        >
          {group.title}
        </td>
      </tr>
    ) : null,
    ...group.lines.map((line) => {
      const idx = globalIdx++;
      const desc = (line.assumptionText ?? line.notes ?? "").toString().trim();
      return (
        <tr key={line.id}>
          <td className={styles.lp}>{String(idx + 1).padStart(2, "0")}</td>
          <td className={styles.nameCell}>
            <div className={styles.itemName}>{line.name}</div>
            {!compact && desc ? <div className={styles.itemDesc}>{desc}</div> : null}
          </td>
          {compact ? null : (
            <>
              <td className={styles.qty}>{line.quantity.toLocaleString("pl-PL")}</td>
              <td className={styles.unit}>{line.unit}</td>
              <td className={styles.price}>{fmtPln(lineUnitNetAfterMargin(line))} zł</td>
            </>
          )}
          <td className={styles.totalCol}>{fmtPln(lineFinalPrice(line))} zł</td>
        </tr>
      );
    }),
  ]);

  return (
    <section className={styles.scopeBlock}>
      <h2 className={styles.scopeBlockHeading}>
        {compact ? "Pozycje — skrót" : "Pozycje kosztorysowe"}{" "}
        <span className={styles.ofScope}>
          · {lines.length} {pluralPozycje(lines.length)}
        </span>
      </h2>
      {lines.length > 0 ? (
        <div className={styles.tableScroll}>
          <table className={styles.items}>
            <thead>
              <tr>
                <th className={styles.lp}>L.p.</th>
                <th>Pozycja</th>
                {compact ? null : (
                  <>
                    <th className={styles.qty}>Ilość</th>
                    <th className={styles.unit}>Jedn.</th>
                    <th className={styles.price}>Cena jedn. netto</th>
                  </>
                )}
                <th className={styles.totalCol}>Wartość netto</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => {
                const desc = (line.assumptionText ?? line.notes ?? "").toString().trim();
                return (
                  <tr key={line.id}>
                    <td className={styles.lp}>{String(idx + 1).padStart(2, "0")}</td>
                    <td className={styles.nameCell}>
                      <div className={styles.itemName}>{line.name}</div>
                      {!compact && desc ? <div className={styles.itemDesc}>{desc}</div> : null}
                    </td>
                    {compact ? null : (
                      <>
                        <td className={styles.qty}>{line.quantity.toLocaleString("pl-PL")}</td>
                        <td className={styles.unit}>{line.unit}</td>
                        <td className={styles.price}>{fmtPln(lineUnitNetAfterMargin(line))} zł</td>
                      </>
                    )}
                    <td className={styles.totalCol}>{fmtPln(lineFinalPrice(line))} zł</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.itemsEmpty}>Brak pozycji - uzupełnij kosztorys.</div>
      )}
    </section>
  );
}
